import { formatString } from "@perceptron/utils/string";

import { getLinkHost } from "@demo/libs/tool-calls/favicon";
import type { ToolCallEntry } from "@demo/store/tool-calls";
import { strings } from "@demo/utils/strings";

/**
 * What a running tool call is doing, for the thinking label while it runs: the search and its query, the
 * page being read, the zoom, or the tool's name.
 */
export function describeToolActivity(call: ToolCallEntry): string {
  const copy = strings.thinkAnnotation.activity;
  const input = call.input;
  // The zoom shares the reverse image search's `image` input, so the family decides before the input does.
  if (call.family === "zoom") return copy.zoom;
  if (call.toolName === "search.image_search" || input?.kind === "image") return copy.imageSearch;
  if (input?.kind === "visit") {
    const hosts = input.urls.map((url) => getLinkHost({ url }) ?? url);
    return hosts.length > 1
      ? formatString(copy.visitMany, hosts[0] ?? "", hosts.length - 1)
      : formatString(copy.visit, hosts[0] ?? "");
  }
  const query = input?.query?.trim();
  if (call.toolName === "search.text_image_search" && query) return formatString(copy.textImageSearch, query);
  if (call.family === "search") return query ? formatString(copy.search, query) : copy.searchWithoutQuery;
  return formatString(copy.generic, call.toolName);
}

/** The call to describe: the most recently started one still running, if any. */
export function runningToolCall(
  order: readonly string[],
  calls: Record<string, ToolCallEntry>,
): ToolCallEntry | undefined {
  for (let i = order.length - 1; i >= 0; i--) {
    const call = calls[order[i]!];
    if (call?.status === "running") return call;
  }
  return undefined;
}
